import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button, Progress, Spinner } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FaFileExcel, 
  FaUpload, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaDownload,
  FaTrash,
  FaEye
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { ApiProcessExcelAreas } from '../../../api/areas';
import ExcelPreview from '../../../components/admin/areas/ExcelPreview';

const CargaExcelAreas = () => {
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [processResult, setProcessResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Configuración del dropzone
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(file);
      setExcelData(null);
      setValidationErrors([]);
      setProcessResult(null);
      validateExcelFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  // Validar archivo Excel
  const validateExcelFile = async (file) => {
    setIsValidating(true);
    setValidationErrors([]);

    try {
      const data = await readExcelFile(file);
      const errors = validateExcelData(data);
      
      if (errors.length === 0) {
        setExcelData(data);
        toast.success('Archivo validado correctamente');
      } else {
        setValidationErrors(errors);
        toast.error('Errores encontrados en el archivo');
      }
    } catch (error) {
      setValidationErrors([`Error al leer el archivo: ${error.message}`]);
      toast.error('Error al procesar el archivo');
    } finally {
      setIsValidating(false);
    }
  };

  // Leer archivo Excel
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Validar datos del Excel
  const validateExcelData = (data) => {
    const errors = [];

    if (!data || data.length === 0) {
      errors.push('El archivo Excel está vacío');
      return errors;
    }

    // Verificar columnas requeridas
    const firstRow = data[0];
    const requiredColumns = ['Cedula', 'Área'];
    const columnVariations = {
      'Cedula': ['Cedula', 'cedula', 'CÉDULA', 'cédula', 'CEDULA', 'cedula'],
      'Área': ['Área', 'area', 'AREA', 'Area', 'área']
    };

    let cedulaColumn = null;
    let areaColumn = null;

    console.warn('Columnas encontradas:', Object.keys(firstRow));

    // Buscar columnas por variaciones
    Object.keys(firstRow).forEach(key => {
      if (columnVariations['Cedula'].includes(key?.trim())) {
        cedulaColumn = key;
      }
      if (columnVariations['Área'].includes(key?.trim())) {
        areaColumn = key;
      }
    });

    if (!cedulaColumn) {
      errors.push('No se encontró la columna "Cedula" (o sus variaciones)');
    }

    if (!areaColumn) {
      errors.push('No se encontró la columna "Área" (o sus variaciones)');
    }

    if (errors.length > 0) {
      return errors;
    }

    // Validar datos
    data.forEach((row, index) => {
      const cedula = row[cedulaColumn];
      const area = row[areaColumn];

      if (!cedula || cedula.toString().trim() === '') {
        errors.push(`Fila ${index + 2}: Cédula vacía o inválida`);
      }

      if (!area || area.toString().trim() === '') {
        errors.push(`Fila ${index + 2}: Área vacía o inválida`);
      }
    });

    return errors;
  };

  // Procesar archivo
  const handleProcessFile = async () => {
    if (!file || !excelData) return;

    setIsProcessing(true);
    setProcessResult(null);

    try {
      const formData = new FormData();
      formData.append('excelFile', file);

      const result = await ApiProcessExcelAreas(formData);
      
      if (result.sms === 'ok') {
        setProcessResult(result);
        toast.success('Archivo procesado correctamente');
        setShowPreview(false);
      } else {
        toast.error(result.mensaje || 'Error al procesar el archivo');
      }
    } catch (error) {
      toast.error('Error al procesar el archivo');
      console.error('Error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Limpiar datos
  const handleClear = () => {
    setFile(null);
    setExcelData(null);
    setValidationErrors([]);
    setProcessResult(null);
    setShowPreview(false);
  };

  // Descargar plantilla
  const downloadTemplate = () => {
    const templateData = [
      { Cedula: '1234567890', 'Área': 'ADMINISTRACION' },
      { Cedula: '0987654321', 'Área': 'PRODUCCION' },
      { Cedula: '1122334455', 'Área': 'SERVICIOS COMUNES' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_areas.xlsx');
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="shadow-sm border-0">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-0 d-flex align-items-center">
                  <FaFileExcel className="me-2" />
                  Carga de Excel - Gestión de Áreas
                </h4>
              </div>
              <div className="card-body p-4">
                {/* Instrucciones */}
                <Alert color="info" className="mb-4">
                  <h6 className="alert-heading">Instrucciones:</h6>
                  <ul className="mb-0">
                    <li>El archivo Excel debe contener las columnas <strong>"Cedula"</strong> y <strong>"Área"</strong></li>
                    <li>Formato de archivo: .xlsx o .xls</li>
                    <li>Tamaño máximo: 5MB</li>
                    <li>Los datos se validarán antes del procesamiento</li>
                  </ul>
                </Alert>

                {/* Botón de descarga de plantilla */}
                <div className="mb-4">
                  <Button 
                    color="outline-primary" 
                    onClick={downloadTemplate}
                    className="me-2"
                  >
                    <FaDownload className="me-1" />
                    Descargar Plantilla
                  </Button>
                </div>

                {/* Zona de carga */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded p-5 text-center ${
                    isDragActive 
                      ? 'border-primary bg-light' 
                      : 'border-secondary'
                  }`}
                  style={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <input {...getInputProps()} />
                  <motion.div
                    animate={{ scale: isDragActive ? 1.05 : 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaUpload size={48} className="text-muted mb-3" />
                    <h5>
                      {isDragActive 
                        ? 'Suelta el archivo aquí' 
                        : 'Arrastra y suelta tu archivo Excel aquí'
                      }
                    </h5>
                    <p className="text-muted">
                      o haz clic para seleccionar un archivo
                    </p>
                    <small className="text-muted">
                      Solo archivos .xlsx, .xls (máx. 5MB)
                    </small>
                  </motion.div>
                </div>

                {/* Archivo seleccionado */}
                {file && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4"
                  >
                    <Card className="border">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <FaFileExcel className="text-success me-2" size={24} />
                            <div>
                              <h6 className="mb-0">{file.name}</h6>
                              <small className="text-muted">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </small>
                            </div>
                          </div>
                          <div>
                            {excelData && (
                              <Button
                                color="outline-info"
                                size="sm"
                                onClick={() => setShowPreview(!showPreview)}
                                className="me-2"
                              >
                                <FaEye className="me-1" />
                                {showPreview ? 'Ocultar' : 'Ver'} Vista Previa
                              </Button>
                            )}
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={handleClear}
                            >
                              <FaTrash className="me-1" />
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                )}

                {/* Errores de validación */}
                <AnimatePresence>
                  {validationErrors.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <Alert color="danger">
                        <h6 className="alert-heading">
                          <FaExclamationTriangle className="me-2" />
                          Errores de Validación:
                        </h6>
                        <ul className="mb-0">
                          {validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Vista previa */}
                <AnimatePresence>
                  {showPreview && excelData && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <ExcelPreview data={excelData} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Resultado del procesamiento */}
                <AnimatePresence>
                  {processResult && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4"
                    >
                      <Alert color="success">
                        <h6 className="alert-heading">
                          <FaCheckCircle className="me-2" />
                          Procesamiento Completado
                        </h6>
                        <p className="mb-2">{processResult.mensaje}</p>
                        {processResult.data && (
                          <div>
                            <p className="mb-1">
                              <strong>Áreas procesadas:</strong> {processResult.data.areasCreated}
                            </p>
                            <p className="mb-1">
                              <strong>Usuarios actualizados:</strong> {processResult.data.usersUpdated}
                            </p>
                            {processResult.data.errors && processResult.data.errors.length > 0 && (
                              <div>
                                <strong>Errores encontrados:</strong>
                                <ul className="mb-0 mt-1">
                                  {processResult.data.errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Botones de acción */}
                <div className="mt-4 d-flex justify-content-between">
                  <Button
                    color="outline-secondary"
                    onClick={handleClear}
                    disabled={isProcessing}
                  >
                    Limpiar Todo
                  </Button>
                  
                  <Button
                    color="primary"
                    onClick={handleProcessFile}
                    disabled={!excelData || validationErrors.length > 0 || isProcessing}
                    className="d-flex align-items-center"
                  >
                    {isProcessing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Procesar Archivo
                      </>
                    )}
                  </Button>
                </div>

                {/* Indicador de validación */}
                {isValidating && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center">
                      <Spinner size="sm" className="me-2" />
                      <span>Validando archivo...</span>
                    </div>
                    <Progress value={100} animated color="info" className="mt-2" />
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        </Col>
      </Row>
    </Container>
  );
};

export default CargaExcelAreas;
